import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Shell from "../components/Shell";
import { api, subjectMeta } from "../lib/api";
import { celebrateAwards } from "../lib/celebrate";
import { toast } from "sonner";
import { CheckCircle, XCircle, ArrowRight, Trophy } from "@phosphor-icons/react";

export default function PracticeTest() {
  const { id } = useParams();
  const nav = useNavigate();
  const [test, setTest] = useState(null);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null); // key
  const [revealed, setRevealed] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [finished, setFinished] = useState(false);
  const [score, setScore] = useState(null);

  useEffect(() => {
    api.get(`/tests/${id}`).then((r) => setTest(r.data)).catch(() => toast.error("Test bulunamadı"));
  }, [id]);

  if (!test) {
    return <Shell title="Test yükleniyor..." hideNav><div className="p-6 text-brand-ink2">Yükleniyor...</div></Shell>;
  }

  const total = test.questions.length;
  const q = test.questions[idx];

  const pickAnswer = (key) => {
    if (revealed) return;
    setSelected(key);
    setRevealed(true);
  };

  const goNext = async () => {
    const newAnswers = [...answers, selected || ""];
    setAnswers(newAnswers);
    setSelected(null);
    setRevealed(false);
    if (idx + 1 >= total) {
      try {
        const { data } = await api.post(`/tests/${id}/submit`, { answers: newAnswers });
        setScore(data.score);
        setFinished(true);
        celebrateAwards(data.awards);
      } catch (e) {
        toast.error("Test gönderilemedi");
      }
    } else {
      setIdx(idx + 1);
    }
  };

  if (finished) {
    const pct = Math.round((score / total) * 100);
    const subjectName = test.subject ? subjectMeta(test.subject).name : "Genel";
    const dateStr = (() => {
      try { return new Date(test.created_at).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" }); }
      catch { return ""; }
    })();
    return (
      <Shell title={`${subjectName} Testi · ${dateStr}`}>
        <div className="flex flex-col items-center text-center py-8 px-4" data-testid="test-finished">
          <div className="w-24 h-24 rounded-full bg-brand-primary flex items-center justify-center mb-4 animate-float">
            <Trophy size={44} weight="fill" color="#1D3557" />
          </div>
          <div className="font-outfit font-extrabold text-3xl text-brand-ink" data-testid="test-score">
            {score} / {total}
          </div>
          <div className="font-nunito text-brand-ink2 mt-1">%{pct} başarı</div>
          <div className="mt-6 grid grid-cols-2 gap-3 w-full">
            <button onClick={() => nav("/app/history")} data-testid="go-history-btn" className="bg-brand-paperAlt text-brand-ink font-outfit font-semibold py-3 rounded-full">Geçmiş</button>
            <button onClick={() => nav("/app")} data-testid="go-home-btn" className="bg-brand-primary text-brand-ink font-outfit font-bold py-3 rounded-full">Ana sayfa</button>
          </div>
        </div>
      </Shell>
    );
  }

  const correct = q.correct_key;
  const opts = q.options;

  return (
    <Shell title={`Soru ${idx + 1} / ${total}`} hideNav>
      <div className="w-full h-2 bg-brand-paperAlt rounded-full overflow-hidden mb-5">
        <div className="h-full bg-brand-primary transition-all" style={{ width: `${((idx) / total) * 100}%` }} data-testid="test-progress" />
      </div>

      <div className="bg-white p-5 rounded-3xl shadow-md border border-black/5 mb-4" data-testid="mcq-card">
        {q.topic && (
          <div className="inline-block text-xs px-3 py-1 rounded-full bg-brand-paperAlt font-nunito font-semibold text-brand-ink2 mb-3">
            {q.topic}
          </div>
        )}
        <div className="font-outfit font-bold text-lg text-brand-ink leading-snug" data-testid="mcq-question-text">{q.question}</div>

        <div className="flex flex-col gap-2.5 mt-5">
          {opts.map((o) => {
            let cls = "border-black/10";
            if (revealed) {
              if (o.key === correct) cls = "border-good bg-good/10";
              else if (o.key === selected) cls = "border-bad bg-bad/10";
              else cls = "border-black/10 opacity-60";
            } else if (selected === o.key) {
              cls = "border-brand-primary bg-brand-primary/10";
            }
            return (
              <button
                key={o.key}
                data-testid={`mcq-option-${o.key}`}
                onClick={() => pickAnswer(o.key)}
                disabled={revealed}
                className={`w-full text-left p-4 rounded-2xl border-2 font-nunito font-semibold text-brand-ink transition-all flex items-start gap-3 ${cls}`}
              >
                <span className="w-7 h-7 rounded-full bg-brand-paperAlt font-outfit font-bold text-sm flex items-center justify-center flex-shrink-0">{o.key}</span>
                <span className="flex-1">{o.text}</span>
                {revealed && o.key === correct && <CheckCircle size={22} weight="fill" color="#2A9D8F" />}
                {revealed && o.key === selected && o.key !== correct && <XCircle size={22} weight="fill" color="#E76F51" />}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div className="mt-5 p-4 rounded-2xl font-nunito text-sm" style={{ background: selected === correct ? "#E9F5F3" : "#FCECE8", color: selected === correct ? "#2A9D8F" : "#E76F51" }} data-testid="mcq-feedback">
            <div className="font-outfit font-bold mb-1">{selected === correct ? "Doğru! 🎉" : "Yanlış."}</div>
            <div style={{ color: "#1D3557" }}>{q.explanation}</div>
          </div>
        )}
      </div>

      <button
        data-testid="mcq-next-button"
        onClick={goNext}
        disabled={!revealed}
        className="w-full bg-brand-primary text-brand-ink font-outfit font-bold text-lg py-4 rounded-full shadow-[0_4px_18px_rgba(255,183,3,0.45)] disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {idx + 1 >= total ? "Bitir" : "Sonraki soru"} <ArrowRight size={20} weight="bold" />
      </button>
    </Shell>
  );
}
